// Coach-Governed Continuity Loop — continuity tension cards.
// Coach-only surface. Dismiss is local state only — not persisted to DB.
// Never auto-resolve tensions. Never show to leaders.

import { useState } from "react";
import { X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import type { ContinuityTension, ContinuityTensionType } from "@/types/continuityTension";

const TYPE_LABELS: Record<ContinuityTensionType, string> = {
  evidence_changed: "Evidence changed",
  mixed_evidence: "Mixed signals",
  agenda_mismatch: "Agenda mismatch",
};

interface ContinuityTensionSectionProps {
  tensions: ContinuityTension[];
}

export function ContinuityTensionSection({ tensions }: ContinuityTensionSectionProps) {
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  const visible = tensions.filter((t) => !dismissed.has(t.id));

  if (visible.length === 0) return null;

  function dismiss(id: string) {
    setDismissed((prev) => new Set([...prev, id]));
  }

  return (
    <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-3">
      <div className="mb-2">
        <p className="text-xs uppercase tracking-wide text-amber-400">Continuity tensions</p>
        <p className="mt-0.5 text-[11px] text-muted-foreground">
          Coach only — review before next session
        </p>
      </div>

      <ul className="space-y-2">
        {visible.map((tension) => (
          <li
            key={tension.id}
            className="rounded border border-amber-500/20 bg-background/40 p-2"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="text-sm font-medium">{tension.reviewTitle}</span>
                  <Badge
                    variant="outline"
                    className="border-amber-500/40 text-[10px] text-amber-400"
                  >
                    {TYPE_LABELS[tension.type]}
                  </Badge>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">{tension.description}</p>
              </div>
              <button
                type="button"
                aria-label={`Dismiss tension for ${tension.reviewTitle}`}
                className="shrink-0 rounded p-0.5 text-muted-foreground hover:bg-muted/60 hover:text-foreground"
                onClick={() => dismiss(tension.id)}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
